/** Coordinate-input parsing for the search box.
 *
 * The search box hands everything to Nominatim, which resolves NAMES — a
 * pasted "33.3152, 44.3661" or a Google-Maps DMS string returns nothing. So
 * coordinates are recognised BEFORE the geocoder and never reach it: a local
 * parse is instant, and the Nominatim proxy is throttled to a shared 1 req/s
 * that live-event resolution also rides.
 *
 * Accepted forms (what people actually paste):
 *   33.3152, 44.3661        decimal, comma/semicolon/space separated
 *   33.3152N 44.3661E       hemisphere letters, before or after, either order
 *   N 33.3152, E 44.3661
 *   33°18'54.7"N 44°21'58.0"E   DMS as Google Maps copies it (smart quotes ok)
 *   33°18.9'N 44°21.9'E         degrees + decimal minutes
 *
 * A bare unlettered pair is read lat, lon; when that can't be right and the
 * swap can (|first| > 90, |second| <= 90) it is read lon, lat and says so via
 * `swapped` — the label must surface that, never silently reorder.
 */

export interface ParsedCoords {
  lat: number;
  lon: number;
  /** The pair arrived lon,lat (no letters, first value out of latitude range)
   * and was reordered. Display code must say so in words. */
  swapped: boolean;
}

interface Part {
  value: number;
  axis: 'lat' | 'lon' | null;   // from a hemisphere letter, else null
}

// One decimal component: optional hemisphere letter on either side, optional
// degree sign. The letter claims an axis; S/W negate.
const DEC_PART = String.raw`([NSEW])?\s*([+-]?\d{1,3}(?:\.\d+)?)\s*°?\s*([NSEW])?`;
// One DMS/DM component: degrees + minutes required, seconds optional, the
// hemisphere letter required (that is what separates it from arithmetic).
const DMS_PART = String.raw`(\d{1,3})\s*°\s*(\d{1,2}(?:\.\d+)?)\s*'\s*(?:(\d{1,2}(?:\.\d+)?)\s*"?\s*)?([NSEW])`;
const SEP = String.raw`(?:\s*[,;]\s*|\s+)`;

const DEC_PAIR = new RegExp(`^\\s*${DEC_PART}${SEP}${DEC_PART}\\s*$`, 'i');
const DMS_PAIR = new RegExp(`^\\s*${DMS_PART}${SEP}${DMS_PART}\\s*$`, 'i');

function axisOf(letter: string | undefined): 'lat' | 'lon' | null {
  if (!letter) return null;
  return 'NS'.includes(letter.toUpperCase()) ? 'lat' : 'lon';
}

function negative(letter: string | undefined): boolean {
  return !!letter && 'SW'.includes(letter.toUpperCase());
}

function decPart(before: string | undefined, num: string, after: string | undefined): Part | null {
  const letter = before || after;
  if (before && after) return null;             // "N33.1E" is not a coordinate
  let value = parseFloat(num);
  if (letter) value = negative(letter) ? -Math.abs(value) : Math.abs(value);
  return { value, axis: axisOf(letter) };
}

function dmsPart(deg: string, min: string, sec: string | undefined, letter: string): Part | null {
  const m = parseFloat(min);
  const s = sec ? parseFloat(sec) : 0;
  if (m >= 60 || s >= 60) return null;
  const value = (parseFloat(deg) + m / 60 + s / 3600) * (negative(letter) ? -1 : 1);
  return { value, axis: axisOf(letter) };
}

/** Two parts -> lat/lon, honouring letters when present, range-checking, and
 * swapping an unlettered lon,lat pair when only that reading is valid. */
function assemble(a: Part, b: Part): ParsedCoords | null {
  let lat: number, lon: number, swapped = false;
  if (a.axis === b.axis && a.axis !== null) return null;   // two latitudes, etc.
  if (a.axis === 'lon' || b.axis === 'lat') { lat = b.value; lon = a.value; }
  else if (a.axis === 'lat' || b.axis === 'lon') { lat = a.value; lon = b.value; }
  else {
    lat = a.value; lon = b.value;
    if (Math.abs(lat) > 90 && Math.abs(lon) <= 90) {
      [lat, lon] = [lon, lat]; swapped = true;
    }
  }
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon, swapped };
}

export function parseCoords(input: string): ParsedCoords | null {
  // Smart quotes (Google Maps copies ′ ″; word processors ’ ”) -> ASCII.
  const q = input.trim().replace(/[′’‘]/g, "'").replace(/[″”“]/g, '"');

  const dms = DMS_PAIR.exec(q);
  if (dms) {
    const a = dmsPart(dms[1]!, dms[2]!, dms[3], dms[4]!);
    const b = dmsPart(dms[5]!, dms[6]!, dms[7], dms[8]!);
    return a && b ? assemble(a, b) : null;
  }

  const dec = DEC_PAIR.exec(q);
  if (dec) {
    const a = decPart(dec[1], dec[2]!, dec[3]);
    const b = decPart(dec[4], dec[5]!, dec[6]);
    return a && b ? assemble(a, b) : null;
  }

  return null;
}

/** One formatting of a parsed pair, lat first — ~1 m precision, enough for
 * anything the imagery can show. */
export function fmtCoords(c: ParsedCoords): string {
  return `${c.lat.toFixed(5)}, ${c.lon.toFixed(5)}`;
}
